import type { NoticeAmendmentView } from '../types';

/**
 * Client-side mirror of server guard **G5**: a notice may not reach `resolved` while a linked
 * amendment is not e-file-accepted. You cannot have resolved a matter whose fix has not landed.
 *
 * ## Why a mirror exists at all
 *
 * The server is authoritative and refuses the PATCH regardless. But the spec's success criterion is
 * that a staff member answers the phone call from ONE screen, and "click resolve, wait, read an
 * error toast" is a second round trip that fails that criterion. Worse, the handoff is explicit that
 * the blocked action must **show its reason, never be a bare disabled button** — a greyed control
 * with no explanation tells the reader nothing about what to do next.
 *
 * So this computes the reason for display, and the server still gets the final word. If the two ever
 * disagree, the server's refusal is what the user sees — the panel surfaces the API error rather
 * than swallowing it.
 *
 * ## The distinction this function exists to preserve
 *
 * `isComplete` is `boolean | null | undefined`. Nullable **on purpose**: a linked return that could
 * not be READ is not a return NOT YET ACCEPTED. Rendering unknown as `false` would make the guard
 * look satisfied on evidence nobody has, and rendering it as `true` would let a notice be resolved
 * against an amendment whose state is a mystery. Unknown BLOCKS, and says so in those words.
 *
 * ⚠ Completeness is read from `isComplete` only — never derived from the raw `status` string.
 * `isComplete` comes from the server's single definition of done (`EfileStatus == "accepted"`),
 * which is a different field from `TaxReturnStatus`. Deriving it from the display status here would
 * be a second, divergent implementation of one invariant.
 */
export function resolveBlockReason(amendments: NoticeAmendmentView[]): string | null {
  const unreadable: number[] = [];
  const notAccepted: number[] = [];

  for (const amendment of amendments) {
    // `!= null` catches both null and undefined; anything else is a real boolean from the server.
    if (amendment.isComplete == null) {
      unreadable.push(amendment.taxYear);
    } else if (!amendment.isComplete) {
      notAccepted.push(amendment.taxYear);
    }
  }

  // Unreadable is reported FIRST, matching the server's precedence, so the panel and the API cannot
  // give the staff member two different reasons for the same refusal.
  if (unreadable.length > 0) {
    return `Cannot resolve: the linked amendment for ${unreadable.join(', ')} could not be read, so its e-file status is unknown.`;
  }

  if (notAccepted.length > 0) {
    const years = notAccepted.join(', ');
    const verb = notAccepted.length === 1 ? 'is' : 'are';
    return `Cannot resolve while the ${years} amendment ${verb} not e-file accepted.`;
  }

  return null;
}

/** Convenience predicate. `resolveBlockReason` is the one to use when you need to SHOW the reason. */
export function canResolve(amendments: NoticeAmendmentView[]): boolean {
  return resolveBlockReason(amendments) === null;
}
